import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Book, Headphones, ShoppingCart, ExternalLink, Sparkles, Send, Star } from 'lucide-react';
import SlidePanel from './SlidePanel';
import { TermsContent, PrivacyContent } from './LegalContent';

// Floating stars
const FloatingStars = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(15)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`
        }}
        animate={{
          y: [-5, 5, -5],
          opacity: [0.2, 0.5, 0.2]
        }}
        transition={{
          duration: 3 + Math.random() * 2,
          delay: Math.random() * 2,
          repeat: Infinity
        }}
      >
        <Star size={6 + Math.random() * 6} className="text-amber-400/20" fill="currentColor" />
      </motion.div>
    ))}
  </div>
);

const Footer = () => {
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !isChecked) return;

    setIsSubmitting(true);
    const BOT_TOKEN = '7792080768:AAGdZ6jv4H4ueprl4aPJpgXRSmuGaxfxcxo';
    const CHAT_ID = '1523610682';
    const message = `New newsletter subscription!\nEmail: ${email}`;

    try {
      const response = await fetch(
        `https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chat_id: CHAT_ID, text: message }),
        }
      );

      if (response.ok) {
        setEmail('');
        setIsChecked(false);
        setSubmitSuccess(true);
        setTimeout(() => setSubmitSuccess(false), 3000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending to Telegram:', error);
      alert('Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const links = [
    {
      icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2zm0 3a7 7 0 100 14 7 7 0 000-14zm0 2a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6z"/>
      </svg>,
      label: 'Apple Podcasts',
      url: 'https://podcasts.apple.com/us/podcast/bite-sized-adventures-with-ned-and-nancy/id1724601433',
      color: 'from-pink-500 to-rose-600'
    },
    {
      icon: <Book className="w-5 h-5" />,
      label: 'eBooks on Google Play',
      url: 'https://play.google.com/store/books/details?id=7UX3EAAAQBAJ&hl=en_US',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: <Headphones className="w-5 h-5" />,
      label: 'Google Audiobooks',
      url: 'https://play.google.com/store/audiobooks/details/Carter_Case_Ned_and_Nancy?id=AQAAAECSZ2wn7M&hl=en_US',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: <ShoppingCart className="w-5 h-5" />,
      label: 'Buy on Amazon',
      url: 'https://a.co/d/ciDjnhM',
      color: 'from-amber-500 to-orange-600'
    }
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a3a 100%)'
          }}
        />

        {/* Animated gradient */}
        <motion.div
          className="absolute inset-0 opacity-30"
          style={{
            background: 'linear-gradient(45deg, #6b4c9a, #14b8a6, #f59e0b, #6b4c9a)',
            backgroundSize: '400% 400%'
          }}
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
          }}
          transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-[#0a0a1a]/80" />

        <FloatingStars />
      </div>

      {/* Top decorative border */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{
          background: 'linear-gradient(90deg, transparent, #6b4c9a, #14b8a6, #f59e0b, #14b8a6, #6b4c9a, transparent)'
        }}
      />

      <div className="relative z-10 px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Logo and description */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <motion.div
                className="relative inline-block"
                whileHover={{ scale: 1.02 }}
              >
                <img
                  src="/images/logo.png"
                  alt="Ned and Nancy"
                  className="h-16 w-auto brightness-0 invert"
                />
                <motion.div
                  className="absolute -top-1 -right-1"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles className="w-4 h-4 text-amber-400" />
                </motion.div>
              </motion.div>

              <p className="text-gray-400 leading-relaxed max-w-sm font-serif">
                Join us on our journey as we explore fascinating stories and share insights
                about life, mystery, and the magic of childhood adventures.
              </p>

              {/* Social proof */}
              <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-teal-500 border-2 border-[#0a0a1a] flex items-center justify-center text-xs font-bold text-white"
                    >
                      {String.fromCharCode(65 + i)}
                    </div>
                  ))}
                </div>
                <span className="text-sm text-gray-400">
                  Join <span className="text-amber-400 font-semibold">50,000+</span> readers
                </span>
              </div>
            </motion.div>

            {/* Listen & Read */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="space-y-6"
            >
              <h4 className="font-display text-xl font-bold gradient-text">
                Listen & Read
              </h4>

              <div className="space-y-3">
                {links.map((link, index) => (
                  <motion.a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ x: 5, scale: 1.02 }}
                    className="group flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/10 transition-all"
                  >
                    <div className={`p-2 rounded-lg bg-gradient-to-br ${link.color}`}>
                      {link.icon}
                    </div>
                    <span className="text-gray-300 group-hover:text-white transition-colors flex-1">
                      {link.label}
                    </span>
                    <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-gray-300 transition-colors" />
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Newsletter */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <div>
                <h4 className="font-display text-xl font-bold gradient-text-gold mb-2">
                  Join Our Newsletter
                </h4>
                <p className="text-gray-400 text-sm">
                  Stay updated with our latest news and magical adventures
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-amber-400 transition-colors" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your Email"
                    className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-amber-400/50 focus:ring-2 focus:ring-amber-400/20 outline-none transition-all"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative mt-0.5">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => setIsChecked(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 rounded border-2 border-gray-600 bg-white/5 peer-checked:border-amber-400 peer-checked:bg-amber-400 transition-all">
                      {isChecked && (
                        <svg className="w-full h-full text-[#0a0a1a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                    I agree to receive email updates and magical mysteries
                  </span>
                </label>

                <motion.button
                  type="submit"
                  disabled={isSubmitting || !email || !isChecked}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-4 px-6 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #fcd34d 100%)'
                  }}
                >
                  {isSubmitting ? (
                    <motion.div
                      className="w-5 h-5 border-2 border-[#0a0a1a] border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                  ) : submitSuccess ? (
                    <>
                      <Star className="w-5 h-5 text-[#0a0a1a]" fill="currentColor" />
                      <span className="text-[#0a0a1a]">Thank You!</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 text-[#0a0a1a]" />
                      <span className="text-[#0a0a1a]">Subscribe Now</span>
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </div>

          {/* Bottom bar */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-16 pt-8 border-t border-white/10"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Legal links */}
              <div className="flex items-center gap-6 text-sm">
                <motion.button
                  onClick={() => setIsTermsOpen(true)}
                  whileHover={{ color: '#f59e0b' }}
                  className="text-gray-400 hover:text-amber-400 transition-colors"
                >
                  Terms
                </motion.button>
                <span className="text-gray-700">|</span>
                <motion.button
                  onClick={() => setIsPrivacyOpen(true)}
                  whileHover={{ color: '#f59e0b' }}
                  className="text-gray-400 hover:text-amber-400 transition-colors"
                >
                  Privacy
                </motion.button>
              </div>

              {/* Copyright */}
              <p className="text-gray-400 text-sm text-center">
                © 2024 Carter Case and Ned and Nancy. All rights reserved.
                <br className="md:hidden" />
                <span className="hidden md:inline"> • </span>
                Built by{' '}
                <a
                  href="https://writetosite.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-400 hover:text-amber-300 transition-colors"
                >
                  WritetoSite
                </a>
                {' & '}
                <a
                  href="https://mysitebroker.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-400 hover:text-amber-300 transition-colors"
                >
                  MySiteBroker
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Terms Panel */}
      <SlidePanel isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} title="Terms of Service">
        <TermsContent />
      </SlidePanel>

      {/* Privacy Panel */}
      <SlidePanel isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} title="Privacy Policy">
        <PrivacyContent />
      </SlidePanel>
    </footer>
  );
};

export default Footer;
